import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Package, Home, ArrowRight } from 'lucide-react';
import api from '../services/api';
import { useCart } from '../context/CartContext';

const OrderSuccessPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const queryOrderId = searchParams.get('order_id');
  const errorParam = searchParams.get('error');
  const codeParam = searchParams.get('code');
  const finalOrderId = orderId || queryOrderId;
  const { refreshCart } = useCart();

  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Detectar si es una página de error o pendiente basándose en la URL
  const isFailure = window.location.pathname.includes('/orden-fallida');
  const isPending = window.location.pathname.includes('/orden-pendiente');

  useEffect(() => {
    // Si es una página de fallo o no hay orderId, no intentar cargar
    if (isFailure || !finalOrderId) {
      setLoading(false);
      return;
    }

    const fetchOrder = async () => {
      try {
        // Intentar obtener la orden. 
        // Si el usuario es invitado, esto fallará por permisos, pero está bien.
        const response = await api.get(`orders/${finalOrderId}/`);
        setOrder(response.data);
      } catch (err) {
        console.error('Error fetching order:', err);
        // Para usuarios invitados, simplemente no mostramos el detalle
        // pero la página de éxito se muestra de todas formas
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
    
    // Refrescar carrito en caso de éxito (vaciar si el pago fue aprobado)
    if (!isFailure && !isPending) {
      refreshCart().catch(err => console.error('Error refreshing cart:', err));
    }
  }, [finalOrderId, isFailure, isPending, refreshCart]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-[72px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Determinar el tipo de mensaje y color
  const getStatusConfig = () => {
    if (isFailure) {
      return {
        bgColor: 'bg-red-600',
        iconBg: 'bg-red-100',
        iconColor: 'text-red-600',
        title: 'Pago No Completado',
        message: errorParam 
          ? `Lo sentimos, tu pago no pudo ser procesado: ${decodeURIComponent(errorParam)}` 
          : 'Lo sentimos, tu pago no pudo ser procesado. Por favor intenta nuevamente.',
        icon: '❌'
      };
    } else if (isPending) {
      return {
        bgColor: 'bg-yellow-600',
        iconBg: 'bg-yellow-100',
        iconColor: 'text-yellow-600',
        title: 'Pago Pendiente',
        message: 'Tu pago está siendo procesado. Te notificaremos cuando se complete.',
        icon: '⏳'
      };
    } else {
      return {
        bgColor: 'bg-green-600',
        iconBg: 'bg-green-100',
        iconColor: 'text-green-600',
        title: '¡Pedido Realizado con Éxito!',
        message: 'Gracias por tu compra. Hemos enviado un correo de confirmación.',
        icon: '✓'
      };
    }
  };

  const config = getStatusConfig();

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 pt-[96px]">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
          <div className={`${config.bgColor} p-8 text-center`}>
            <div className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full ${config.iconBg} mb-4`}>
              <span className="text-4xl">{config.icon}</span>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">{config.title}</h1>
            <p className="text-white text-opacity-90 text-lg">
              {config.message}
            </p>
          </div>

          <div className="p-8">
            {order ? (
              <div className="space-y-8">
                <div className="border-b border-gray-200 pb-6">
                  <h2 className="text-lg font-medium text-gray-900 mb-4">Detalles del Pedido {order.public_code || (order.id ? `#${order.id.slice(0, 8)}` : '')}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-gray-500">Estado</p>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                        {order.status === 'processing' ? 'Procesando' : order.status}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Fecha</p>
                      <p className="font-medium text-gray-900 mt-1">
                        {new Date(order.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium text-gray-900 mt-1">{order.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total</p>
                      <p className="font-medium text-gray-900 mt-1 text-xl">
                        ${Number(order.total_amount).toLocaleString('es-CL')}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Productos</h3>
                  <ul className="divide-y divide-gray-200">
                    {order.items?.map((item: any) => (
                      <li key={item.id} className="py-4 flex items-center">
                        <div className="flex-shrink-0 h-16 w-16 bg-gray-100 rounded-md overflow-hidden">
                           {/* Si tuviéramos imagen en el item o producto anidado */}
                           <Package className="h-full w-full p-4 text-gray-400" />
                        </div>
                        <div className="ml-4 flex-1">
                          <h4 className="text-sm font-medium text-gray-900">{item.product_name || 'Producto'}</h4>
                          {item.variant_name && <p className="text-sm text-gray-500">{item.variant_name}</p>}
                          <p className="text-sm text-gray-500">Cant: {item.quantity}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            ${Number(item.total_price).toLocaleString('es-CL')}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">
                  Tu pedido ha sido procesado correctamente. Si tienes alguna duda, revisa tu correo electrónico.
                </p>
                {codeParam && (
                    <p className="text-sm text-gray-900 font-medium">Código de pedido: {codeParam}</p>
                )}
                {finalOrderId && (
                    <p className="text-sm text-gray-500">ID de referencia: {finalOrderId}</p>
                )}
              </div>
            )}

            <div className="mt-8 flex justify-center space-x-4">
              <Link
                to="/"
                className="inline-flex items-center px-6 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
              >
                <Home className="mr-2 h-5 w-5" />
                Volver al Inicio
              </Link>
              {isFailure ? (
                <Link
                  to="/checkout"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
                >
                  Reintentar Pago
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              ) : (
                <Link
                  to="/products"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
                >
                  Seguir Comprando
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccessPage;
